import { getCrmColors } from "../../theme/crmColors";
import React, { useState, useCallback, useContext, useEffect, useRef, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles, ThemeProvider, useTheme } from "@material-ui/core/styles";
import TicketsManager from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { CircularProgress } from "@material-ui/core";
import { getBackendUrl } from "../../config";
import logo from "../../assets/cchat-logo-transparent.png";
import logoDark from "../../assets/cchat-logo-transparent-dark.png";
import useTicketsFit from "./useTicketsFit";
import { DEFAULT_LIST_WIDTH, MIN_SAVED_LIST_WIDTH, clampListWidth, getDraggedListWidth } from "./fitLayout";
import FitAutocompletePopper from "./FitAutocompletePopper";

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: theme.spacing(1.5),
		height: `calc(100% - 48px)`,
		overflowY: "hidden",
		background: theme.mode === "light" ? "#f4f7fb" : (theme.ticketDark ? getCrmColors(theme, "dark").background : "#424242"),
		[theme.breakpoints.down("sm")]: {
			padding: theme.spacing(1),
		},
	},
	chatPapper: {
		display: "flex",
		height: "100%",
		gap: theme.spacing(1.5),
		minWidth: 0,
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		flexShrink: 0,
		overflowY: "hidden",
		position: "relative",
		borderRadius: 8,
		background: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").surface : "#2f2f2f"),
		border: theme.mode === "light" ? "1px solid #d9e2ef" : (theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : "1px solid rgba(255,255,255,0.08)"),
		boxShadow: theme.mode === "light" ? "0 14px 34px rgba(15, 23, 42, 0.08)" : "0 18px 42px rgba(0,0,0,0.18)",
	},
	messagesWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		flexGrow: 1,
		minWidth: 0,
		borderRadius: 8,
		overflow: "hidden",
		background: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").background : "#2b2b2b"),
		border: theme.mode === "light" ? "1px solid #d9e2ef" : (theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : "1px solid rgba(255,255,255,0.08)"),
		boxShadow: theme.mode === "light" ? "0 14px 34px rgba(15, 23, 42, 0.08)" : "0 18px 42px rgba(0,0,0,0.18)",
	},
	welcomeMsg: {
		background: theme.mode === "light" ? "linear-gradient(135deg, #ffffff 0%, #eef5ff 100%)" : (theme.ticketDark ? getCrmColors(theme, "dark").surface : "#2f2f2f"),
		color: theme.mode === "light" ? "#142033" : (theme.ticketDark ? "#FFFFFF" : "#f8fafc"),
		display: "flex",
		justifyContent: "space-evenly",
		alignItems: "center",
		height: "100%",
		textAlign: "center",
		border: 0,
		fontWeight: 700,
	},
	dragger: {
		width: "6px",
		cursor: "ew-resize",
		padding: "4px 0 0",
		borderTop: 0,
		position: "absolute",
		top: 0,
		right: 0,
		bottom: 0,
		zIndex: 100,
		backgroundColor: theme.mode === "light" ? "#e6edf6" : (theme.ticketDark ? getCrmColors(theme, "dark").raised : "#3a3a3a"),
		transition: "background-color 0.2s ease",
		"&:hover": {
			backgroundColor: theme.palette.primary.main,
		},
		userSelect: "none", // Evita a seleção de texto no elemento de redimensionamento
	},
	logo: {
		filter: theme.ticketDark && [logo, logoDark].includes(theme.calculatedLogoDark()) ? "brightness(0) invert(1)" : "none",
		logo: theme.logo,
		content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")"
	},
}));

const TicketsCustom = () => {
	const { user } = useContext(AuthContext);
	const baseTheme = useTheme();
	const fittedTheme = useMemo(() => ({
		...baseTheme,
		props: {
			...baseTheme.props,
			MuiAutocomplete: { ...baseTheme.props?.MuiAutocomplete, PopperComponent: FitAutocompletePopper },
		},
	}), [baseTheme]);

	const classes = useStyles();

	const { ticketId } = useParams();

	const [ticketsManagerWidth, setTicketsManagerWidth] = useState(DEFAULT_LIST_WIDTH);
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);
	const contactsWrapperRef = useRef(null);
	const dragCleanupRef = useRef(null);
	const containerRef = useRef(null);
	const fit = useTicketsFit(containerRef);
	const fitRef = useRef(fit);
	fitRef.current = fit;
	const displayListWidth = clampListWidth(ticketsManagerWidth, fit.minListWidth, fit.maxListWidth);

	useEffect(() => {
		const nextWidth = clampListWidth(user?.defaultTicketsManagerWidth, MIN_SAVED_LIST_WIDTH);
		ticketsManagerWidthRef.current = nextWidth;
		setTicketsManagerWidth(nextWidth);
	}, [user]);
	useEffect(() => () => {
		if (dragCleanupRef.current) dragCleanupRef.current();
	}, []);

	// useEffect(() => {
	// 	if (ticketId && currentTicket.uuid === undefined) {
	// 		history.push("/tickets");
	// 	}
	// }, [ticketId, currentTicket.uuid, history]);

	const handleMouseDown = (e) => {
		e.preventDefault();
		if (dragCleanupRef.current) dragCleanupRef.current();
		dragCleanupRef.current = () => {
			document.removeEventListener("mouseup", handleMouseUp, true);
			document.removeEventListener("mousemove", handleMouseMove, true);
		};
		document.addEventListener("mouseup", handleMouseUp, true);
		document.addEventListener("mousemove", handleMouseMove, true);
	};
	const handleSaveContact = async value => {
		const width = clampListWidth(value, MIN_SAVED_LIST_WIDTH);
		await api.put(`/users/toggleChangeWidht/${user.id}`, { defaultTicketsManagerWidth: width });

	}
	const handleMouseMove = useCallback(
		(e) => {
			const wrapperLeft = contactsWrapperRef.current?.getBoundingClientRect().left || 0;
			const { scale, minListWidth, maxListWidth } = fitRef.current;
			const width = getDraggedListWidth(e.clientX, wrapperLeft, scale, minListWidth, maxListWidth);
			ticketsManagerWidthRef.current = width;
			setTicketsManagerWidth(width);
		},
		[]
	);

	const handleMouseUp = async () => {
		if (dragCleanupRef.current) dragCleanupRef.current();
		dragCleanupRef.current = null;

		const newWidth = ticketsManagerWidthRef.current;

		if (newWidth !== ticketsManagerWidth) {
			await handleSaveContact(newWidth);
		}
	};

	return (
		<ThemeProvider theme={fittedTheme}>
		<QueueSelectedProvider>
			<div ref={containerRef} className={classes.chatContainer} data-tickets-viewport>
				<div className={classes.chatPapper} style={{ zoom: fit.scale }} data-tickets-scale={fit.scale}>
					<div
						ref={contactsWrapperRef}
						className={classes.contactsWrapper}
						style={{
							width: displayListWidth,
							flexBasis: displayListWidth,
							minWidth: fit.minListWidth,
							maxWidth: fit.maxListWidth,
						}}
					>
						<TicketsManager />
						<div onMouseDown={e => handleMouseDown(e)} className={classes.dragger} />
					</div>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<>
								{/* <Suspense fallback={<CircularProgress />}> */}
								<Ticket />
								{/* </Suspense> */}
							</>
						) : (
							<Hidden only={["sm", "xs"]}>
								<Paper square variant="outlined" className={classes.welcomeMsg}>
									<span>
										<center>
											<img className={classes.logo} width="50%" alt="" />
										</center>
										{i18n.t("chat.noTicketMessage")}
									</span>								</Paper>
							</Hidden>
						)}
					</div>
				</div>
			</div>
		</QueueSelectedProvider>
		</ThemeProvider>
	);
};

export default TicketsCustom;
